import merge from "lodash-es/merge";
import omit from "lodash-es/omit";
import { MAX_VEHICLES } from "src/constants";
import { putConditionalRebatesRequest } from "src/store/actions/conditionalRebates";
import { patchPaymentsFailure, patchPaymentsRequest, patchPaymentsSuccess } from "src/store/actions/payments";
import { pollingSuccess } from "src/store/actions/poller";
import { ActionType } from "src/store/dataService/actions/utils/actionCreator";
import { requestFinished, requestStarted } from "src/utils";
import { VehicleState } from "Types/state";
import { authenticationMergeSuccess, getDealSuccess, UpdateDealVehiclesAction } from "../actions/deal";
import {
    deleteVehicleFailure,
    deleteVehicleRequest,
    deleteVehicleSuccess,
    postVehicleFailure,
    postVehicleRequest,
    postVehicleSuccess,
} from "../actions/vehicles";
import { UPDATE_DEAL_VEHICLES } from "../constants";

export interface VehicleReducer {
    entities: {
        [vin: string]: VehicleState;
    };
    order: string[];
}

function reducer(
    state: VehicleReducer = {
        entities: {},
        order: [],
    },
    action:
        | UpdateDealVehiclesAction // TODO: Delete action and refactor related api calls
        | ActionType<typeof pollingSuccess>
        | ActionType<typeof getDealSuccess>
        | ActionType<typeof postVehicleRequest>
        | ActionType<typeof postVehicleSuccess>
        | ActionType<typeof postVehicleFailure>
        | ActionType<typeof patchPaymentsRequest>
        | ActionType<typeof patchPaymentsSuccess>
        | ActionType<typeof patchPaymentsFailure>
        | ActionType<typeof deleteVehicleRequest>
        | ActionType<typeof deleteVehicleSuccess>
        | ActionType<typeof deleteVehicleFailure>
        | ActionType<typeof authenticationMergeSuccess>
        | ActionType<typeof putConditionalRebatesRequest>
): VehicleReducer {
    switch (action.type) {
        case pollingSuccess.type:
        case getDealSuccess.type:
        case authenticationMergeSuccess.type:
            return {
                entities: {
                    ...Object.values(state.entities).reduce((entities, vehicle) => {
                        if (vehicle.loading) {
                            return { ...entities, [vehicle.vin]: vehicle };
                        }
                        return entities;
                    }, {}),
                    ...action.payload.deal.vehicles?.reduce((entities, vehicle) => {
                        if (state.entities[vehicle.vin]?.isUpdating || state.entities[vehicle.vin]?.requests > 0) {
                            return { ...entities, [vehicle.vin]: state.entities[vehicle.vin] };
                        }
                        return { ...entities, [vehicle.vin]: { ...vehicle, isUpdating: false, requests: 0 } };
                    }, {}),
                },
                order: action.payload.deal.vehicles?.length
                    ? [
                          ...(action.payload.deal.vehicles?.map((vehicle) => vehicle.vin) ?? false),
                          ...Object.values(state.entities)
                              .filter((vehicle) => vehicle.loading)
                              .map((vehicle) => vehicle.vin),
                      ].filter(Boolean)
                    : state.order,
            };

        case postVehicleRequest.type:
            // Short-circuit if:
            // 1. We already have the max amount of vehicles in the garage
            // 2. The vehicle already exists in state. However, if the user re-adds it (add, delete, add)
            //    and the delete hasn't finished, allow the re-add to occur which will wipe out the isDeleting
            if (
                (action.payload.vin in state.entities &&
                    !action.payload.currentVehicles[action.payload.vin]?.isDeleting) ||
                state.order.length >= MAX_VEHICLES
            ) {
                return state;
            }

            const order = state.order.includes(action.payload.vin) ? state.order : [...state.order, action.payload.vin];

            return {
                entities: {
                    ...state.entities,
                    [action.payload.vin]: {
                        vin: action.payload.vin,
                        payments: { selected: action.payload.paymentType },
                        loading: true,
                        isUpdating: false,
                        requests: 1,
                    } as VehicleState,
                },
                order,
            };

        case postVehicleSuccess.type: {
            return {
                entities: {
                    ...state.entities,
                    [action.payload.vehicle.vin]: {
                        ...action.payload.vehicle,
                        loading: false,
                        isUpdating: false,
                        requests: 0,
                    },
                },
                order: [...state.order],
            };
        }

        case postVehicleFailure.type:
            if (action.payload.reason === "vehicle_exists" || action.payload.reason === "vehicle_limit") {
                return state;
            }

            return {
                entities: omit(state.entities, action.payload.vin),
                order: state.order.filter((vin) => vin !== action.payload.vin),
            };

        case patchPaymentsRequest.type:
            // don't update if delete is in progress or the vin isn't in the garage
            if (!(action.payload.vin in state.entities) || state.entities[action.payload.vin]?.isDeleting) {
                return state;
            }

            // If we are just switching the selected payment type, and the payment type we are switching to has offers,
            // don't set isUpdating
            if (
                Object.keys(action.payload.payments).length === 1 &&
                action.payload.payments.selected !== undefined &&
                (state.entities[action.payload.vin].payments[action.payload.payments.selected]?.offers?.length ?? 0) > 0
            ) {
                return {
                    ...state,
                    entities: {
                        ...state.entities,
                        [action.payload.vin]: {
                            ...state.entities[action.payload.vin],
                            payments: {
                                ...merge(state.entities[action.payload.vin].payments, action.payload.payments),
                            },
                            ...requestStarted(state.entities[action.payload.vin], false),
                        },
                    },
                };
            }

            return {
                ...state,
                entities: {
                    ...state.entities,
                    [action.payload.vin]: {
                        ...state.entities[action.payload.vin],
                        payments: {
                            ...merge(state.entities[action.payload.vin].payments, action.payload.payments),
                        },
                        ...requestStarted(state.entities[action.payload.vin]),
                    },
                },
            };

        case patchPaymentsSuccess.type:
            // don't update if delete is in progress or the vin isn't in the garage
            if (!(action.payload.vin in state.entities) || state.entities[action.payload.vin]?.isDeleting) {
                return state;
            }

            return {
                ...state,
                // @ts-ignore TS is confused...
                entities: {
                    ...state.entities,
                    [action.payload.vin]: {
                        ...state.entities[action.payload.vin],
                        payments: {
                            ...state.entities[action.payload.vin].payments,
                            loan: {
                                ...state.entities[action.payload.vin].payments.loan,
                                request_hash: action.payload.payments.loan?.request_hash,
                                data_hash: action.payload.payments.loan?.data_hash,
                                selected_offer: action.payload.payments.loan?.selected_offer,
                                offers: action.payload.payments.loan?.offers,
                            },
                            lease: {
                                ...state.entities[action.payload.vin].payments.lease,
                                request_hash: action.payload.payments.lease?.request_hash,
                                data_hash: action.payload.payments.lease?.data_hash,
                                selected_offer: action.payload.payments.lease?.selected_offer,
                                offers: action.payload.payments.lease?.offers,
                            },
                            cash: {
                                ...state.entities[action.payload.vin].payments.cash,
                                request_hash: action.payload.payments.cash?.request_hash,
                                data_hash: action.payload.payments.cash?.data_hash,
                                selected_offer: action.payload.payments.cash?.selected_offer,
                                offers: action.payload.payments.cash?.offers,
                            },
                        },
                        ...requestFinished(state.entities[action.payload.vin]),
                    },
                },
            };

        case patchPaymentsFailure.type:
            return {
                ...state,
                entities: {
                    ...state.entities,
                    [action.payload.vin]: {
                        ...state.entities[action.payload.vin],
                        ...requestFinished(state.entities[action.payload.vin]),
                    },
                },
            };

        case deleteVehicleRequest.type:
            // don't update if delete is in progress or the vin isn't in the garage
            if (!(action.payload.vin in state.entities) || state.entities[action.payload.vin]?.isDeleting) {
                return state;
            }

            return {
                ...state,
                entities: {
                    ...state.entities,
                    [action.payload.vin]: {
                        ...state.entities[action.payload.vin],
                        isDeleting: true,
                        ...requestStarted(state.entities[action.payload.vin]),
                    },
                },
            };

        case deleteVehicleSuccess.type:
            // if the vehicle was re-added, don't delete from state
            if (!state.entities[action.payload.vin]?.isDeleting) {
                return state;
            }

            return {
                entities: omit(state.entities, action.payload.vin),
                order: state.order.filter((vin) => vin !== action.payload.vin),
            };

        case deleteVehicleFailure.type:
            return {
                ...state,
                entities: {
                    ...state.entities,
                    [action.payload.vin]: {
                        ...state.entities[action.payload.vin],
                        isDeleting: false,
                        ...requestFinished(state.entities[action.payload.vin]),
                    },
                },
            };

        case UPDATE_DEAL_VEHICLES:
            return {
                entities: {
                    ...action.data.vehicles?.reduce((entities, vehicle) => {
                        return {
                            ...entities,
                            [vehicle.vin]: { ...vehicle, isUpdating: false, requests: 0 },
                        };
                    }, {}),
                },
                order: action.data.vehicles.map((vehicle) => vehicle.vin),
            };

        case putConditionalRebatesRequest.type:
            return {
                ...state,
                entities: {
                    ...Object.values(state.entities).reduce((entities, vehicle) => {
                        return {
                            ...entities,
                            [vehicle.vin]: {
                                ...vehicle,
                                payments: {
                                    ...vehicle.payments,
                                    [vehicle.payments.selected]: {
                                        ...vehicle.payments[vehicle.payments.selected],
                                        request_hash: null,
                                    },
                                },
                            },
                        };
                    }, {}),
                },
            };

        default:
            return state;
    }
}

export default reducer;
